import { Store } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useGetAmazonLogs, useProcessPoints } from '@/features/amazon/amazon.queries';
import { formatDateTime } from '@/utils/format';
import type { DispatchPlace } from '@/features/dispatch-places/dispatch-places.types';

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  PLANNED: { label: 'Planeado', className: 'badge-info' },
  ACTIVE: { label: 'Activo', className: 'badge-success' },
  INACTIVE: { label: 'Inactivo', className: 'badge-neutral' },
  PROCESSING: { label: 'Procesando', className: 'badge-warning' },
  NONE: { label: 'Sin configuración', className: 'badge-ghost' },
};

const ACTION_LABELS: Record<number, string> = {
  1: 'Planear',
  2: 'Activar',
  3: 'Desactivar',
  4: 'Actualizar',
};

interface AmazonPointPanelProps {
  place: DispatchPlace;
}

export function AmazonPointPanel(props: AmazonPointPanelProps) {
  const { place } = props;
  const { data: logsData, isLoading: isLoadingLogs } = useGetAmazonLogs(place.id);
  const processMutation = useProcessPoints();

  const statusKey = place.amazon?.processing_status === 1
    ? 'PROCESSING'
    : (place.amazon?.status || 'NONE');
  const config = STATUS_CONFIG[statusKey];
  const is_processing = statusKey === 'PROCESSING';

  const onAction = (action: number) => {
    if (window.confirm(`¿Estás seguro de que deseas ejecutar la acción "${ACTION_LABELS[action]}"?`)) {
      processMutation.mutate({
        dispatch_place_ids: [place.id],
        action
      });
    }
  };

  const renderActionButton = (action: number) => {
    let disabled = is_processing || processMutation.isPending;
    const status = place.amazon?.status;

    if (!status) {
      if (action !== 1) disabled = true;
    } else if (status === 'PLANNED') {
      if (action === 1) disabled = true;
    } else if (status === 'ACTIVE') {
      if (action === 1 || action === 2) disabled = true;
    } else if (status === 'INACTIVE') {
      if (action !== 2) disabled = true;
    }

    return (
      <button
        key={action}
        disabled={disabled}
        onClick={() => onAction(action)}
        className={twMerge(
          "btn btn-xs",
          disabled ? "btn-disabled" : "btn-outline btn-primary"
        )}
      >
        {ACTION_LABELS[action]}
      </button>
    );
  };

  return (
    <div className="card border border-base-300 bg-base-100 h-full">
      <div className="card-body gap-4">
        <div className="flex items-center justify-between">
          <h2 className="card-title text-base flex items-center gap-2">
            <Store className="h-5 w-5 text-primary" />
            Amazon Hub
          </h2>
          <span className={twMerge('badge badge-sm', config.className)}>
            {config.label}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          {[1, 2, 3, 4].map(renderActionButton)}
        </div>

        <div className="divider my-0">Logs de actividad</div>

        <div className="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
          {isLoadingLogs ? (
            <div className="flex justify-center py-4">
              <span className="loading loading-spinner loading-md text-primary" />
            </div>
          ) : logsData?.items && logsData.items.length === 0 ? (
            <div className="text-center py-4 text-xs opacity-50">
              Sin actividad registrada.
            </div>
          ) : (
            logsData?.items.map((log, idx) => (
              <div key={idx} className="text-xs border-b border-base-300 last:border-0 pb-2">
                <div className="flex justify-between font-medium">
                  <span className={log.error ? 'text-error' : 'text-primary'}>
                    {ACTION_LABELS[log.action] || 'Evento'}
                  </span>
                  <span className="opacity-50 font-normal">
                    {formatDateTime(log.created_at)}
                  </span>
                </div>
                <p className="opacity-70 mt-0.5">{log.event_description}</p>
                {log.error_description && (
                  <p className="text-error mt-1 italic">{log.error_description}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
